import {
  CheckIcon,
  Flex,
  Form,
  FormSearchSelection,
  FormSelection,
  FormTextarea,
  FormTextField,
  type OptionSelectionProps,
  ReadonlyField,
  Text,
} from '@shared/components';
import { FormProvider, type UseFormReturn } from 'react-hook-form';

import { MIDDLE_NAME_HELPER_TEXT } from '../../constants';
import {
  type AlternativeForeignerRegistrationFormOutput,
  type AlternativeForeignerRegistrationFormInput,
} from '../../schemas';
import { FormImageInput, type FormImageInputProps } from '../form-image-input';

interface AlternativeForeignerRegistrationFormField {
  name: keyof AlternativeForeignerRegistrationFormInput;
}

export interface AlternativeForeignerRegistrationFormProps {
  form: UseFormReturn<
    AlternativeForeignerRegistrationFormInput,
    unknown,
    AlternativeForeignerRegistrationFormOutput
  >;
  onSubmit?: (data: AlternativeForeignerRegistrationFormOutput) => void;
  slotProps: {
    nameTitleField: AlternativeForeignerRegistrationFormField & { options: OptionSelectionProps[] };
    firstNameField: AlternativeForeignerRegistrationFormField;
    middleNameField: AlternativeForeignerRegistrationFormField;
    lastNameField: AlternativeForeignerRegistrationFormField;
    documentTypeField: AlternativeForeignerRegistrationFormField;
    countryCodeField: AlternativeForeignerRegistrationFormField & {
      options: OptionSelectionProps[];
    };
    passportNumberField: AlternativeForeignerRegistrationFormField;
    documentImageField: AlternativeForeignerRegistrationFormField &
      Pick<FormImageInputProps, 'onClick'>;
    causeField: AlternativeForeignerRegistrationFormField;
  };
}

export default function AlternativeForeignerRegistrationForm(
  props: Readonly<AlternativeForeignerRegistrationFormProps>,
) {
  const { form, onSubmit, slotProps } = props;
  const {
    nameTitleField,
    firstNameField,
    middleNameField,
    lastNameField,
    documentTypeField,
    countryCodeField,
    passportNumberField,
    documentImageField,
    causeField,
  } = slotProps;

  const { getValues } = form;

  return (
    <FormProvider {...form}>
      <Form onSubmit={onSubmit ? form.handleSubmit(onSubmit) : undefined}>
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
          {/* Name Title */}
          <FormSelection
            label='คำนำหน้า'
            placeholder='เลือกคำนำหน้า'
            required
            itemComponent={(componentsProps) => {
              const isSelected = getValues('nameTitle') === componentsProps.option.value;
              return (
                <Flex sx={{ width: '100%', justifyContent: 'space-between' }}>
                  <Text>{componentsProps.option.label}</Text>
                  {isSelected ? <CheckIcon color='primary' sx={{ fontSize: '24px' }} /> : null}
                </Flex>
              );
            }}
            sx={{
              flex: 1,
            }}
            {...nameTitleField}
          />

          {/* First Name, Middle Name & Last Name */}
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
              flexDirection: 'column',
              [theme.breakpoints.up('tablet')]: {
                flexDirection: 'row',
              },
            })}
          >
            <FormTextField
              label='ชื่อ'
              placeholder='กรอกชื่อ'
              required
              sx={{ flex: 1 }}
              {...firstNameField}
            />
            <FormTextField
              label='ชื่อกลาง'
              placeholder='กรอกชื่อกลาง'
              helperText={MIDDLE_NAME_HELPER_TEXT}
              sx={{ flex: 1 }}
              {...middleNameField}
            />
            <FormTextField
              label='นามสกุล'
              placeholder='กรอกนามสกุล'
              required
              sx={{ flex: 1 }}
              {...lastNameField}
            />
          </Flex>

          {/* Document Type */}
          <ReadonlyField
            label='ประเภทเอกสาร'
            content={<Text>พาสปอร์ต</Text>}
            {...documentTypeField}
          />

          {/* Passport Number */}
          <ReadonlyField
            label='เลขพาสปอร์ต'
            content={
              <Text>
                {(() => {
                  const val = getValues(passportNumberField.name);
                  return typeof val === 'string' ? val : (val?.label ?? '');
                })()}
              </Text>
            }
            {...passportNumberField}
          />

          {/* Country Code */}
          <FormSearchSelection
            label='รหัสประเทศ'
            placeholder='กรอกรหัสประเทศ'
            noOptionsText='ไม่พบรหัสประเทศดังกล่าว'
            required
            sx={{
              minWidth: '200px',
            }}
            {...countryCodeField}
          />

          {/* Document image */}
          <FormImageInput label='ภาพถ่ายเอกสาร' required {...documentImageField} />

          {/* Cause */}
          <FormTextarea
            label='สาเหตุ'
            required
            slotProps={{
              htmlInput: {
                sx: {
                  resize: 'vertical',
                },
              },
            }}
            {...causeField}
          />
        </Flex>
      </Form>
    </FormProvider>
  );
}
